<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import type { UFPMMember, UFPMSubTab } from '../lib/types'
import type { PlanData } from '@/features/ai-plan/lib/types'
import type { ScoreResult } from '@/features/scoring/lib/types'
import AIPlanIsland from '@/features/ai-plan/AIPlanIsland.vue'
import UFPMTestPanel from './UFPMTestPanel.vue'
import { scoreEntryToResult } from '../lib/scoreEntryToResult'
import { planDisplayName, exportPlanJson, sharePlanJson, exportPlanPdf, exportPlanXlsx } from '../lib/ufpmPlanExport'

const props = defineProps<{
  member: UFPMMember
  subTab: UFPMSubTab
}>()

const emit = defineEmits<{
  'update:subTab': [tab: UFPMSubTab]
  'back':          []
  'editMember':    []
  'deleteMember':  []
  'switchToCalc':  []
  'switchToPlan':  []
}>()

const SUBTABS: Array<{ id: UFPMSubTab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'test',     label: 'Test' },
  { id: 'plan',     label: 'Training' },
  { id: 'history',  label: 'History' },
]

// Canvas for inline history mini-chart
const histCanvasRef = ref<HTMLCanvasElement | null>(null)
let histRO: ResizeObserver | null = null

// Latest values (prefer history[0], fallback to member root fields)
const latestEntry = computed(() => props.member.history?.[0] ?? null)

const latestScoreForPlan = computed<ScoreResult | null>(() => {
  const entry = latestEntry.value
  if (!entry) return null
  return scoreEntryToResult(entry, props.member)
})

const latestComposite = computed(() => {
  const raw = latestEntry.value?.composite ?? props.member.compositeScore
  const n = raw != null ? parseFloat(String(raw)) : NaN
  return Number.isFinite(n) ? n : null
})
const latestCompositeLabel = computed(() =>
  latestComposite.value !== null ? latestComposite.value.toFixed(1) : '—'
)

const latestCategory = computed(() =>
  latestEntry.value?.cat ?? props.member.latestFitnessAssessmentCategory ?? null
)

// Filtered numeric history for stats + chart (preserves source order: newest first)
const numericHistory = computed(() =>
  (props.member.history ?? [])
    .map(e => ({ ...e, num: parseFloat(String(e.composite)) }))
    .filter(e => Number.isFinite(e.num))
)

const historyStats = computed(() => {
  const nums = numericHistory.value.map(e => e.num)
  if (!nums.length) return null
  const latest = nums[0]!
  const prev   = nums[1] ?? null
  const best   = Math.max(...nums)
  const delta  = prev !== null ? latest - prev : null
  return { count: nums.length, latest, best, delta }
})

const profileDisplayName = computed(() => {
  const name = props.member.fullName
    || [props.member.firstName, props.member.lastName].filter(Boolean).join(' ')
  const rank = props.member.rank
  return [name, rank].filter(Boolean).join(', ') || 'Selected Member'
})

// Color class helpers (tie to existing CSS vars via our new rules)
function scoreClass(score: number | null): string {
  if (score === null || !Number.isFinite(score)) return ''
  if (score >= 90) return 'score-band--excellent'
  if (score >= 75) return 'score-band--satisfactory'
  return 'score-band--unsatisfactory'
}

function catClass(cat: string | null | undefined): string {
  const c = (cat || '').toUpperCase().trim()
  if (!c) return ''
  if (c === 'EXCELLENT' || c.startsWith('EXC')) return 'ufpm-cat-tag--excellent'
  if (c === 'SATISFACTORY' || c.startsWith('SAT')) return 'ufpm-cat-tag--satisfactory'
  if (c === 'UNSATISFACTORY' || c.startsWith('UNSAT') || c === 'POOR') return 'ufpm-cat-tag--unsatisfactory'
  return ''
}

function memberMeta(m: UFPMMember): string {
  return [m.rank, m.organization, m.age ? `Age ${m.age}` : '', m.fitnessCurrency]
    .filter(Boolean).join(' · ')
}

function formatDate(d?: string | number | null): string {
  if (!d) return '—'
  try {
    const dt = new Date(d)
    if (!isNaN(dt.getTime())) return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  } catch {}
  return String(d)
}

function handlePlanGenerated(plan: PlanData) {
  if (window.ufpmTools?.savePlanForSelectedMember(plan)) {
    window.showToast?.('Plan saved for ' + props.member.fullName)
  }
}

function drawHistChart() {
  const canvas = histCanvasRef.value
  if (!canvas) return
  const wrap = canvas.parentElement
  const ctx = canvas.getContext('2d')
  if (!ctx || !wrap) return

  const cssW = Math.max(1, Math.floor(wrap.clientWidth || 320))
  const cssH = 130
  const dpr  = Math.min(window.devicePixelRatio || 1, 2)
  canvas.width  = Math.floor(cssW * dpr)
  canvas.height = Math.floor(cssH * dpr)
  canvas.style.width  = cssW + 'px'
  canvas.style.height = cssH + 'px'
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

  // oldest → newest for chronological left-to-right
  const data = [...numericHistory.value].reverse()
  const scores = data.map(h => h.num)
  const W = cssW, H = cssH
  const pad = { t: 18, r: 16, b: 22, l: 32 }
  const chartW = W - pad.l - pad.r
  const chartH = H - pad.t - pad.b

  const css = getComputedStyle(document.body)
  const textColor   = css.getPropertyValue('--text').trim()   || '#c5cdd8'
  const mutedColor  = css.getPropertyValue('--muted').trim()  || '#5a6a7a'
  const accentColor = css.getPropertyValue('--accent').trim() || '#a07840'
  const greenColor  = css.getPropertyValue('--green').trim()  || '#2ecc71'
  const redColor    = css.getPropertyValue('--red').trim()    || '#e74c3c'

  ctx.clearRect(0, 0, W, H)

  if (scores.length < 2) {
    ctx.fillStyle = mutedColor
    ctx.font = '11px Barlow'
    ctx.textAlign = 'center'
    ctx.fillText('Need 2+ scores to chart', W / 2, H / 2 + 4)
    return
  }

  const min    = Math.max(0, Math.min(...scores) - 8)
  const max    = Math.min(100, Math.max(...scores) + 8)
  const xScale = (i: number) => pad.l + i * (chartW / (scores.length - 1))
  const yScale = (v: number) => pad.t + chartH - ((v - min) / (max - min)) * chartH

  // Threshold guides at 75 and 90
  ;[75, 90].forEach(v => {
    if (v >= min && v <= max) {
      ctx.strokeStyle = v === 90 ? 'rgba(46,204,113,0.28)' : 'rgba(232,160,32,0.28)'
      ctx.lineWidth = 1
      ctx.setLineDash([3, 3])
      ctx.beginPath()
      ctx.moveTo(pad.l, yScale(v))
      ctx.lineTo(W - pad.r, yScale(v))
      ctx.stroke()
      ctx.setLineDash([])
      ctx.fillStyle = v === 90 ? 'rgba(46,204,113,0.65)' : 'rgba(232,160,32,0.65)'
      ctx.font = '9px Barlow Condensed'
      ctx.textAlign = 'right'
      ctx.fillText(String(v), pad.l - 3, yScale(v) + 3)
    }
  })

  // Connecting line
  ctx.strokeStyle = accentColor
  ctx.lineWidth = 1.75
  ctx.setLineDash([])
  ctx.beginPath()
  scores.forEach((v, i) => { i === 0 ? ctx.moveTo(xScale(i), yScale(v)) : ctx.lineTo(xScale(i), yScale(v)) })
  ctx.stroke()

  // Points + value labels
  scores.forEach((v, i) => {
    ctx.fillStyle = v >= 90 ? greenColor : v >= 75 ? accentColor : redColor
    ctx.beginPath()
    ctx.arc(xScale(i), yScale(v), 3.5, 0, Math.PI * 2)
    ctx.fill()

    ctx.fillStyle = textColor
    ctx.font = '9px Barlow Condensed'
    ctx.textAlign = 'center'
    ctx.fillText(v.toFixed(0), xScale(i), yScale(v) - 7)
  })

  // X-axis dates (compact)
  ctx.fillStyle = mutedColor
  ctx.font = '9px Barlow'
  ctx.textAlign = 'center'
  data.forEach((h, i) => {
    const raw = h.recordedDate || h.date || ''
    const short = (raw || '').replace(/^\d{4}-/, '').slice(0, 5) // MM-DD preferred
    ctx.fillText(short || '—', xScale(i), H - 4)
  })
}

// Redraw on tab activation (handles v-show zero-size) + data change + resize
watch(() => props.subTab, (tab) => {
  if (tab === 'history') nextTick(() => drawHistChart())
})
watch(() => props.member.history, () => { drawHistChart() }, { deep: true })

onMounted(() => {
  drawHistChart()
  if (histCanvasRef.value?.parentElement) {
    histRO = new ResizeObserver(() => drawHistChart())
    histRO.observe(histCanvasRef.value.parentElement)
  }
})
onUnmounted(() => { histRO?.disconnect() })
</script>

<template>
  <div class="ufpm-profile-view">

    <!-- Topbar: back + compact actions -->
    <div class="ufpm-profile-topbar">
      <button
        class="ufpm-profile-back-link"
        type="button"
        aria-label="Back to roster"
        @click="emit('back')"
      >‹ Back</button>
      <div class="ufpm-profile-actions">
        <button class="btn btn-secondary ufpm-profile-action" type="button" @click="emit('editMember')">Edit</button>
        <button class="btn btn-danger    ufpm-profile-action" type="button" @click="emit('deleteMember')">Delete</button>
      </div>
    </div>

    <!-- Identity block with color-coded score chip -->
    <div class="ufpm-profile-identity">
      <div class="ufpm-profile-id-row">
        <span class="ufpm-profile-score-chip" :class="scoreClass(latestComposite)">{{ latestCompositeLabel }}</span>
        <span class="ufpm-profile-name">{{ profileDisplayName }}</span>
      </div>
      <div class="ufpm-profile-meta">
        <span v-if="latestCategory" class="tag ufpm-cat-tag" :class="catClass(latestCategory)">{{ latestCategory }}</span>
        <span>{{ memberMeta(props.member) || '—' }}</span>
      </div>
    </div>

    <!-- Compact status strip (Next Due + Currency only — Score & Category live in the identity block above) -->
    <div class="ufpm-profile-status">
      <div class="ufpm-status-item">
        <span class="ufpm-status-label">Next Due</span>
        <span class="ufpm-status-value">{{ props.member.nextFitnessAssessmentDueDate || '—' }}</span>
      </div>
      <div class="ufpm-status-item">
        <span class="ufpm-status-label">Currency</span>
        <span class="ufpm-status-value">{{ props.member.fitnessCurrency || '—' }}</span>
      </div>
    </div>

    <!-- Sub-tab nav (unchanged styles & behavior) -->
    <div class="ufpm-subtabs" role="tablist" aria-label="UFPM member tools">
      <button
        v-for="tab in SUBTABS"
        :key="tab.id"
        class="ufpm-subtab"
        :class="{ active: subTab === tab.id }"
        type="button"
        role="tab"
        :aria-selected="subTab === tab.id"
        @click="emit('update:subTab', tab.id)"
      >{{ tab.label }}</button>
    </div>

    <!-- Overview → Readiness Snapshot -->
    <div v-show="subTab === 'overview'" class="ufpm-subpanel">
      <div class="ufpm-snapshot-grid">
        <div class="ufpm-snapshot-item">
          <span class="ufpm-snapshot-label">Tests Recorded</span>
          <span class="ufpm-snapshot-value">{{ props.member.history?.length || 0 }}</span>
        </div>
        <div class="ufpm-snapshot-item">
          <span class="ufpm-snapshot-label">Latest Test</span>
          <span class="ufpm-snapshot-value">{{ formatDate(latestEntry?.recordedDate || latestEntry?.date || props.member.lastTestDate) }}</span>
        </div>
        <div class="ufpm-snapshot-item">
          <span class="ufpm-snapshot-label">Best Score</span>
          <span class="ufpm-snapshot-value" :class="scoreClass(historyStats?.best ?? null)">{{ historyStats ? historyStats.best.toFixed(1) : '—' }}</span>
        </div>
        <div class="ufpm-snapshot-item">
          <span class="ufpm-snapshot-label">Saved Plans</span>
          <span class="ufpm-snapshot-value">{{ props.member.plans?.length || 0 }}</span>
        </div>
      </div>

      <div v-if="props.member.plans?.length">
        <div class="ufpm-plans-header">Recent Plans</div>
        <div v-for="entry in props.member.plans" :key="entry.id" class="ufpm-plan-row">
          <strong>{{ planDisplayName(entry.plan as Record<string,unknown>) }}</strong>
          <div class="ufpm-plan-actions">
            <button class="btn btn-secondary ufpm-profile-action" type="button" @click="exportPlanPdf(entry.plan as Record<string,unknown>, props.member.fullName)">PDF</button>
            <button class="btn btn-secondary ufpm-profile-action" type="button" @click="exportPlanXlsx(entry.plan as Record<string,unknown>, props.member.fullName)">Excel</button>
            <button class="btn btn-secondary ufpm-profile-action" type="button" @click="exportPlanJson(entry.plan as Record<string,unknown>, props.member.fullName)">JSON</button>
            <button class="btn btn-secondary ufpm-profile-action" type="button" @click="sharePlanJson(entry.plan as Record<string,unknown>, props.member.fullName)">Share</button>
          </div>
          <span style="font-size:11px;color:var(--muted);">{{ (entry.plan as Record<string,unknown>).total_weeks || '-' }} wks · {{ entry.createdAt ? new Date(entry.createdAt).toLocaleDateString() : '' }}</span>
        </div>
      </div>
      <div v-else class="ufpm-empty" style="margin-top:4px;">No saved plans yet.</div>
    </div>

    <!-- Administer Test (workflow context card) -->
    <div v-show="subTab === 'test'" class="ufpm-subpanel">
      <UFPMTestPanel :key="'test-' + props.member.id" :member="props.member" @saved="emit('update:subTab', 'history')" />
      <div v-if="false" class="card">
        <div class="card-title">Member Context</div>
        <div class="ufpm-workflow-rows">
          <div class="ufpm-workflow-row"><span class="ufpm-workflow-label">Age</span><span class="ufpm-workflow-value">{{ props.member.age ?? '—' }}</span></div>
          <div class="ufpm-workflow-row"><span class="ufpm-workflow-label">Sex</span><span class="ufpm-workflow-value">{{ props.member.sex || '—' }}</span></div>
          <div class="ufpm-workflow-row"><span class="ufpm-workflow-label">Latest</span><span class="ufpm-workflow-value">{{ latestCompositeLabel }}<span v-if="latestCategory"> ({{ latestCategory }})</span></span></div>
          <div class="ufpm-workflow-row"><span class="ufpm-workflow-label">Due</span><span class="ufpm-workflow-value">{{ props.member.nextFitnessAssessmentDueDate || '—' }}</span></div>
        </div>
        <div class="ufpm-workflow-steps">
          ✓ Confirm age/sex bracket in Readiness<br>
          ✓ Record exemptions before scoring<br>
          ✓ Save score — it will attach to this member
        </div>
        <button class="btn btn-primary" type="button" @click="emit('switchToCalc')">Open Dashboard</button>
      </div>
    </div>

    <!-- Generate Plan (workflow context card) -->
    <div v-show="subTab === 'plan'" class="ufpm-subpanel">
      <AIPlanIsland
        :key="'plan-' + props.member.id"
        :score-override="latestScoreForPlan"
        :persist-active-plan="false"
        :show-load-action="false"
        :show-import-shortcut="false"
        :show-export-actions="false"
        :context-label="props.member.fullName || 'selected member'"
        @plan-generated="handlePlanGenerated"
      />
      <div v-if="false" class="card">
        <div class="card-title">Training Basis</div>
        <div class="ufpm-workflow-rows">
          <div class="ufpm-workflow-row"><span class="ufpm-workflow-label">Score</span><span class="ufpm-workflow-value">{{ latestCompositeLabel }}<span v-if="latestCategory"> ({{ latestCategory }})</span> — {{ formatDate(latestEntry?.recordedDate || latestEntry?.date) }}</span></div>
          <div class="ufpm-workflow-row"><span class="ufpm-workflow-label">Plans</span><span class="ufpm-workflow-value">{{ props.member.plans?.length || 0 }} saved</span></div>
        </div>
        <div class="ufpm-workflow-steps">
          ✓ Uses your latest saved composite score<br>
          ✓ Generated plan saved to this member profile<br>
          ✓ Previous plans remain attached
        </div>
        <button class="btn btn-primary" type="button" @click="emit('switchToPlan')">Open Training</button>
        <p v-if="!latestComposite" class="ufpm-empty" style="margin-top:8px;font-size:11px;">Tip: Saving a score first improves plan personalization.</p>
      </div>
    </div>

    <!-- History (summary + mini chart + compact list) -->
    <div v-show="subTab === 'history'" class="ufpm-subpanel">
      <div v-if="historyStats" class="ufpm-history-summary">
        Entries: <strong>{{ historyStats.count }}</strong>
        Latest: <strong :class="scoreClass(historyStats!.latest)">{{ historyStats!.latest.toFixed(1) }}</strong>
        Best: <strong :class="scoreClass(historyStats!.best)">{{ historyStats!.best.toFixed(1) }}</strong>
        <template v-if="historyStats!.delta != null">
          Trend: <strong :style="{ color: historyStats!.delta >= 0 ? 'var(--green)' : 'var(--red)' }">{{ historyStats!.delta >= 0 ? '+' : '' }}{{ historyStats!.delta.toFixed(1) }}</strong>
        </template>
      </div>

      <div class="ufpm-hist-chart-wrap">
        <canvas ref="histCanvasRef"></canvas>
      </div>

      <div v-if="numericHistory.length">
        <div
          v-for="entry in numericHistory"
          :key="entry.id"
          class="ufpm-hcl-item"
        >
          <span class="ufpm-hcl-score" :class="scoreClass(entry.num)">{{ typeof entry.composite === 'number' ? entry.composite.toFixed(1) : entry.composite }}</span>
          <span class="tag ufpm-cat-tag" :class="catClass(entry.cat)">{{ entry.cat || '—' }}</span>
          <span class="ufpm-hcl-date">{{ formatDate(entry.recordedDate || entry.date) }}</span>
          <span class="ufpm-hcl-breakdown">
            PU: {{ entry.puEx ? 'EXM' : (entry.puScore ?? '—') }} ·
            Core: {{ entry.coreEx ? 'EXM' : (entry.coreScore ?? '—') }} ·
            Cardio: {{ entry.cardioEx ? 'EXM' : (entry.cardioScore ?? '—') }} ·
            WHtR: {{ entry.whtrEx ? 'EXM' : (entry.whtrScore ?? '—') }}
          </span>
        </div>
      </div>
      <div v-else class="ufpm-empty">
        No scores saved yet.
        <button class="btn btn-primary ufpm-profile-action" style="margin-left:6px;" type="button" @click="emit('update:subTab', 'test')">Record Test</button>
      </div>
    </div>

  </div>
</template>
